// Didaktische Bausteine für Arbeitsblätter.
// Jeder Baustein liefert sauberes HTML mit .ws-* Klassen, das in contentEditable
// stabil bleibt und beim Drucken (Vektor-PDF) korrekt umbricht.

export type BlockType =
  | 'task'
  | 'rule'
  | 'answer'
  | 'cloze'
  | 'mc'
  | 'truefalse'
  | 'match'
  | 'mathgrid'
  | 'info'
  | 'table'
  | 'divider';

export interface BlockDef {
  type: BlockType;
  label: string;
  hint: string;
}

// Reihenfolge im „Einfügen"-Menü
export const BLOCK_DEFS: BlockDef[] = [
  { type: 'task', label: 'Aufgabe', hint: 'Nummerierter Arbeitsauftrag' },
  { type: 'rule', label: 'Schreiblinien', hint: 'Lineatur zum Schreiben' },
  { type: 'answer', label: 'Antwortlinien', hint: 'Linien für Antworten' },
  { type: 'cloze', label: 'Lückentext', hint: 'Text mit Lücken' },
  { type: 'mc', label: 'Multiple-Choice', hint: 'Zum Ankreuzen' },
  { type: 'truefalse', label: 'Richtig / Falsch', hint: 'Aussagen bewerten' },
  { type: 'match', label: 'Zuordnung', hint: 'Verbinden / zuordnen' },
  { type: 'mathgrid', label: 'Rechenkästchen', hint: 'Kariertes Rechenfeld' },
  { type: 'info', label: 'Merkkasten', hint: 'Hervorgehobener Hinweis' },
  { type: 'table', label: 'Tabelle', hint: '2×2 Tabelle' },
  { type: 'divider', label: 'Trennlinie', hint: 'Abschnitt trennen' },
];

export type RuleVariant = 'haus' | 'kl2' | 'simple';

function ruleRows(count: number): string {
  return Array.from({ length: count }, () => '<div class="ws-rule-row"></div>').join('');
}

export function ruleBlock(variant: RuleVariant, rows = 4): string {
  return `<div class="ws-rule ws-rule--${variant}" contenteditable="false">${ruleRows(rows)}</div>`;
}

export function answerBlock(rows = 3): string {
  return `<div class="ws-rule ws-rule--answer" contenteditable="false">${ruleRows(rows)}</div>`;
}

export function imageFigure(dataUrl: string): string {
  return (
    `<figure class="ws-figure">` +
    `<img src="${dataUrl}" alt="" />` +
    `<figcaption>Bildunterschrift …</figcaption>` +
    `</figure><p><br></p>`
  );
}

// ── Mathe-Generator ──────────────────────────────────────────
export type MathOp = 'add' | 'sub' | 'mul' | 'div';

const rand = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

export function mathProblems(op: MathOp, max: number, count: number): string {
  const items: string[] = [];
  for (let i = 0; i < count; i++) {
    let a: number, b: number, sign: string;
    if (op === 'add') {
      a = rand(0, max); b = rand(0, Math.max(0, max - a)); sign = '+';
    } else if (op === 'sub') {
      a = rand(0, max); b = rand(0, a); sign = '−';
    } else if (op === 'mul') {
      const top = Math.min(10, Math.max(2, max));
      a = rand(1, top); b = rand(1, top); sign = '·';
    } else {
      b = rand(1, 10); const res = rand(1, Math.max(1, Math.floor(max / b))); a = b * res; sign = ':';
    }
    items.push(`<div class="ws-mathitem">${a} ${sign} ${b} = <span class="ws-gap"></span></div>`);
  }
  return `<div class="ws-mathgen" contenteditable="false">${items.join('')}</div><p><br></p>`;
}

// ── Vorlagen ─────────────────────────────────────────────────
export interface Template {
  id: string;
  label: string;
  title: string;
  html: string;
}

export const TEMPLATES: Template[] = [
  {
    id: 'lesen',
    label: 'Leseblatt',
    title: 'Lesetext',
    html:
      '<h2>Lesetext</h2><p>Hier den Lesetext einfügen …</p>' +
      blockHtmlSafe('task') +
      '<h3>Beantworte die Fragen:</h3>' + answerBlock(4),
  },
  {
    id: 'rechnen',
    label: 'Rechenblatt',
    title: 'Rechenblatt',
    html:
      '<h2>Rechne aus</h2>' + mathProblems('add', 20, 8) +
      '<h3>Knobelaufgabe</h3>' + blockHtmlSafe('task'),
  },
  {
    id: 'wochenplan',
    label: 'Wochenplan',
    title: 'Mein Wochenplan',
    html:
      '<h2>Mein Wochenplan</h2>' +
      '<table class="ws-table"><tbody>' +
      ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag']
        .map((d) => `<tr><td style="width:32%"><strong>${d}</strong></td><td>&nbsp;</td></tr>`)
        .join('') +
      '</tbody></table><p><br></p>',
  },
  {
    id: 'lzk',
    label: 'Lernzielkontrolle',
    title: 'Lernzielkontrolle',
    html:
      '<div class="ws-info"><span class="ws-info-label" contenteditable="false">Hinweis</span><p>Bearbeite alle Aufgaben sorgfältig. Du hast 45 Minuten Zeit. Viel Erfolg!</p></div>' +
      blockHtmlSafe('task') + blockHtmlSafe('task') + blockHtmlSafe('task') +
      '<p style="text-align:right">Erreichte Punkte: <span class="ws-gap" contenteditable="false"></span> / <span class="ws-gap" contenteditable="false"></span></p>',
  },
];

// interne Hilfsfunktion (vermeidet Vorwärtsreferenz auf blockHtml)
function blockHtmlSafe(type: BlockType): string {
  return blockHtml(type);
}

export function blockHtml(type: BlockType): string {
  switch (type) {
    case 'task':
      return (
        `<div class="ws-task">` +
        `<div class="ws-task-num" contenteditable="false"></div>` +
        `<div class="ws-task-body"><p><strong>Arbeitsauftrag:</strong> Schreibe hier deine Aufgabenstellung …</p></div>` +
        `</div><p><br></p>`
      );

    case 'rule':
      return ruleBlock('haus', 4) + '<p><br></p>';

    case 'answer':
      return answerBlock(3) + '<p><br></p>';

    case 'cloze':
      return (
        `<p class="ws-cloze">Der Hund läuft <span class="ws-gap" contenteditable="false"></span> über die <span class="ws-gap" contenteditable="false"></span>.</p>` +
        `<div class="ws-wordbank" contenteditable="false"><span class="ws-wordbank-label">Wörter:</span> <span class="ws-word">schnell</span><span class="ws-word">Wiese</span></div>` +
        `<p><br></p>`
      );

    case 'mc':
      return (
        `<div class="ws-mc">` +
        `<p class="ws-mc-q">Frage: Welche Antwort ist richtig?</p>` +
        `<label class="ws-opt"><span class="ws-box" contenteditable="false"></span><span>Antwort A</span></label>` +
        `<label class="ws-opt"><span class="ws-box" contenteditable="false"></span><span>Antwort B</span></label>` +
        `<label class="ws-opt"><span class="ws-box" contenteditable="false"></span><span>Antwort C</span></label>` +
        `</div><p><br></p>`
      );

    case 'truefalse':
      return (
        `<table class="ws-tf"><thead><tr><th>Aussage</th><th>richtig</th><th>falsch</th></tr></thead>` +
        `<tbody>` +
        `<tr><td>Aussage 1 …</td><td class="ws-tf-box" contenteditable="false"></td><td class="ws-tf-box" contenteditable="false"></td></tr>` +
        `<tr><td>Aussage 2 …</td><td class="ws-tf-box" contenteditable="false"></td><td class="ws-tf-box" contenteditable="false"></td></tr>` +
        `</tbody></table><p><br></p>`
      );

    case 'match':
      return (
        `<div class="ws-match">` +
        `<ul class="ws-match-col"><li>Begriff 1</li><li>Begriff 2</li><li>Begriff 3</li></ul>` +
        `<ul class="ws-match-col ws-match-col--right"><li>Erklärung A</li><li>Erklärung B</li><li>Erklärung C</li></ul>` +
        `</div><p><br></p>`
      );

    case 'mathgrid':
      return `<div class="ws-mathgrid" contenteditable="false"></div><p><br></p>`;

    case 'info':
      return (
        `<div class="ws-info"><span class="ws-info-label" contenteditable="false">Merke</span>` +
        `<p>Wichtiger Hinweis oder Regel zum Merken …</p></div><p><br></p>`
      );

    case 'table':
      return (
        `<table class="ws-table"><tbody>` +
        `<tr><td>&nbsp;</td><td>&nbsp;</td></tr>` +
        `<tr><td>&nbsp;</td><td>&nbsp;</td></tr>` +
        `</tbody></table><p><br></p>`
      );

    case 'divider':
      return `<hr class="ws-hr" contenteditable="false" /><p><br></p>`;

    default:
      return '';
  }
}
